import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { subscribe } from "@/lib/events";
import { Users, Loader2, AlertTriangle, CheckCircle2, HelpCircle, RefreshCw, ExternalLink, Ruler, TrendingUp } from "lucide-react";

/**
 * PeerWinsCalibrationCard — surfaces the peer-wins price band the Estimating
 * Agent will calibrate against for this opportunity. Zero credit spend.
 *
 * Props:
 *   noticeId  — opportunity notice_id (required)
 *   liveTotal — number, latest grand_total from the Estimate page so the
 *               verdict chip recomputes as the operator edits unit prices.
 */
const money = (v) =>
  v == null || isNaN(Number(v)) ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fixed2 = (v) =>
  v == null || isNaN(Number(v)) ? "—" : `$${Number(v).toFixed(2)}`;

export default function PeerWinsCalibrationCard({ noticeId, liveTotal }) {
  const [calib, setCalib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(false);

  const load = async ({ isRescore = false } = {}) => {
    if (!isRescore) setLoading(true);
    try {
      const r = await api.get(`/peer-wins/calibration/${noticeId}`);
      setCalib(r.data);
      if (isRescore) {
        setFlash(true);
        setTimeout(() => setFlash(false), 1400);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    load();
    const unsub = subscribe("peer_wins:changed", () => {
      if (alive) load({ isRescore: true });
    });
    return () => {
      alive = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  if (loading) {
    return (
      <div className="bg-[#0D1424] border border-white/10 p-5" data-testid="peer-wins-loading">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Calibrating against peer wins…
        </div>
      </div>
    );
  }

  const total = calib?.total_in_library ?? 0;
  const matched = calib?.matched_count ?? 0;
  const percentiles = calib?.percentiles;
  const psf = calib?.dollar_per_sf;
  const top = calib?.top_matches || [];

  // Empty state — no peer wins in library
  if (total === 0) {
    return (
      <div className="bg-[#0D1424] border border-sky-400/40 p-5" data-testid="peer-wins-empty">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-400/15 text-sky-300 flex items-center justify-center shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="mono-label text-sky-300 mb-1">// PEER WINS</div>
            <div className="font-display font-bold text-lg text-white">
              No peer-win corpus yet
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Upload a CSV of real winning awards from friends, industry peers, or your own past wins so the
              Estimator sizes bids against actual dollars, not just industry averages.
            </p>
            <Link
              to="/pricing-history?tab=peer-wins"
              className="inline-flex items-center gap-2 mt-3 text-xs font-mono font-bold tracking-widest bg-sky-400 text-slate-900 hover:bg-sky-300 px-4 py-2 rounded-full"
              data-testid="peer-wins-add-cta"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              UPLOAD PEER WINS
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Verdict against liveTotal (if any) vs peer percentile band
  let verdict = null;
  if (percentiles && liveTotal != null && Number(liveTotal) > 0) {
    const lt = Number(liveTotal);
    if (lt < percentiles.p25 * 0.5)
      verdict = {
        tid: "peer-wins-verdict-low",
        cls: "border-rose-400/40 text-rose-200 bg-rose-500/5",
        icon: HelpCircle,
        msg: `LIVE ${money(lt)} is well below peer band (${money(percentiles.p25)}–${money(percentiles.p75)}). You may be leaving money on the table.`,
      };
    else if (lt > percentiles.p75 * 1.5)
      verdict = {
        tid: "peer-wins-verdict-high",
        cls: "border-amber-400/40 text-amber-200 bg-amber-500/5",
        icon: AlertTriangle,
        msg: `LIVE ${money(lt)} is well above peer band (${money(percentiles.p25)}–${money(percentiles.p75)}). Worth double-checking scope or dropping the price.`,
      };
    else
      verdict = {
        tid: "peer-wins-verdict-ok",
        cls: "border-emerald-400/40 text-emerald-200 bg-emerald-500/5",
        icon: CheckCircle2,
        msg: `LIVE ${money(lt)} lands inside the peer band (${money(percentiles.p25)}–${money(percentiles.p75)}). Bid is competitive.`,
      };
  }

  return (
    <div
      className={`bg-[#0D1424] border p-5 transition-all ${flash ? "border-sky-400/70 ring-2 ring-sky-400/30" : "border-white/10"}`}
      data-testid="peer-wins-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="mono-label text-sky-300 mb-1 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            // PEER WINS CALIBRATION
            {flash && (
              <span
                className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest bg-sky-500/15 text-sky-300 border border-sky-400/40 px-2 py-0.5 rounded-full"
                data-testid="peer-wins-rescored-flash"
              >
                <RefreshCw className="w-3 h-3" />
                RESCORED
              </span>
            )}
          </div>
          <div className="font-display font-bold text-lg text-white">
            Calibrated against {matched} of {total} peer win{total === 1 ? "" : "s"}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Real award totals your peers submitted for similar scope — the Estimator uses these as its strongest
            calibration anchor.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => load({ isRescore: true })}
            className="text-slate-400 hover:text-sky-300 border border-white/10 hover:border-sky-400/50 rounded-full p-1.5"
            title="Recalibrate"
            data-testid="peer-wins-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <Link
            to="/pricing-history?tab=peer-wins"
            className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-sky-300 border border-white/10 hover:border-sky-400/50 rounded-full px-3 py-1.5"
            data-testid="peer-wins-manage"
          >
            MANAGE CORPUS
          </Link>
        </div>
      </div>

      {/* Verdict banner (only shows when liveTotal is passed in) */}
      {verdict && (
        <div className={`border rounded-lg px-4 py-3 mb-4 flex items-start gap-2.5 text-sm ${verdict.cls}`} data-testid={verdict.tid}>
          <verdict.icon className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{verdict.msg}</div>
        </div>
      )}

      {/* Percentile band */}
      {percentiles && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="peer-wins-percentiles">
          <Percent label="P25" value={money(percentiles.p25)} />
          <Percent label="MEDIAN" value={money(percentiles.median)} accent />
          <Percent label="P75" value={money(percentiles.p75)} />
        </div>
      )}

      {/* $/SF band */}
      {psf && (
        <div className="border border-emerald-400/30 bg-emerald-500/5 rounded-lg px-4 py-3 mb-4 flex items-center gap-3" data-testid="peer-wins-per-sf">
          <Ruler className="w-4 h-4 text-emerald-300 shrink-0" />
          <div className="text-sm">
            <span className="text-slate-300">Known peer $/SF for this scope: </span>
            <span className="font-mono font-bold text-emerald-300">
              {fixed2(psf.min)} – {fixed2(psf.max)}
            </span>
            <span className="text-slate-400"> (median {fixed2(psf.median)} · n={psf.count})</span>
          </div>
        </div>
      )}

      {/* Top matches */}
      {top.length > 0 && (
        <ul className="space-y-1.5" data-testid="peer-wins-list">
          {top.map((m) => (
            <li
              key={m.item_id}
              className="flex items-start gap-3 border border-white/5 bg-white/[0.02] rounded-lg px-3 py-2"
              data-testid={`peer-wins-row-${m.item_id}`}
            >
              <span
                className="inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest bg-sky-500/15 text-sky-300 border border-sky-400/40 rounded-full px-2 py-0.5 shrink-0"
                title={`Relevance score ${m.match_score}`}
              >
                {m.match_score}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm text-white">
                  <span className="font-mono font-bold text-emerald-300 shrink-0">{money(m.award_amount)}</span>
                  <span className="text-slate-400 text-xs">·</span>
                  <span className="truncate">{m.agency || "Agency n/a"}{m.state ? ` · ${m.state}` : ""}</span>
                  {m.link && (
                    <a
                      href={m.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-500 hover:text-sky-300 shrink-0"
                      title="View on USAspending"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {m.description && (
                  <div className="text-xs text-slate-500 truncate">{m.description}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Percent({ label, value, accent = false }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        accent ? "border-sky-400/40 bg-sky-500/5" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400">{label}</div>
      <div className={`font-mono font-bold ${accent ? "text-sky-300" : "text-white"}`}>{value}</div>
    </div>
  );
}
